import React from "react";

import {
  TableCellData,
  CellChangeParams,
  ButtonClickParams,
  TableCellType,
} from "./types";
import { TitleCell } from "./cells/TitleCell";
import { FixedCell } from "./cells/FixedCell";
import { ListCell } from "./cells/ListCell";
import { InputCell } from "./cells/InputCell";
import { DropdownCell } from "./cells/DropdownCell";
import { ImageCell } from "./cells/ImageCell";
import { MathJaxCell } from "./cells/MathJaxCell";
import { ButtonCell } from "./cells/ButtonCell";

/* -------------------------------- Types -------------------------------- */

export interface CellRenderContext {
  cellId: string;
  cell: TableCellData;
  rowIndex: number;
  colIndex: number;
  isCorrect: boolean;
  isPrediction?: boolean;
  isSubmitted?: boolean;
  isIncomplete?: boolean;
  /** Table-level showFeedback fallback when cell.showFeedback is not set */
  showFeedback?: boolean;
  /**
   * Accessible name computed from the row/column headers for editable
   * (INPUT/DROPDOWN) cells. Used only when the cell has no authored
   * `cell.ariaLabel`, which takes precedence.
   */
  ariaLabel?: string;
  striped?: boolean;
  /** Header scope for TITLE cells: "col" for column headers, "row" for row labels. */
  scope?: "col" | "row";
  onCellChange: (params: Omit<CellChangeParams, "componentID">) => void;
  onButtonClick: (params: Omit<ButtonClickParams, "componentID">) => void;
}

type CellRenderFunction = (context: CellRenderContext) => React.ReactNode;

/* -------------------------------- Cell Renderers -------------------------------- */

const renderTitleCell: CellRenderFunction = ({ cellId, cell, scope }) => (
  <TitleCell
    key={cellId}
    cellId={cellId}
    text={cell.text}
    colspan={cell.colspan}
    bgColor={cell.bgColor}
    align={cell.align}
    scope={scope}
    ariaLabel={cell.ariaLabel}
  />
);

const renderFixedCell: CellRenderFunction = ({
  cellId,
  cell,
  rowIndex,
  striped,
}) => (
  <FixedCell
    key={cellId}
    cellId={cellId}
    text={cell.text}
    colspan={cell.colspan}
    align={cell.align}
    isZebra={striped && rowIndex % 2 === 1}
    bgColor={cell.bgColor}
    ariaLabel={cell.ariaLabel}
  />
);

const renderListCell: CellRenderFunction = ({ cellId, cell }) => (
  <ListCell
    key={cellId}
    cellId={cellId}
    text={cell.text}
    colspan={cell.colspan}
    bgColor={cell.bgColor}
    ariaLabel={cell.ariaLabel}
  />
);

const renderTextAlignedCell: CellRenderFunction = ({
  cellId,
  cell,
  rowIndex,
  striped,
}) => (
  <FixedCell
    key={cellId}
    cellId={cellId}
    text={cell.text}
    colspan={cell.colspan}
    align="center"
    isZebra={striped && rowIndex % 2 === 1}
    bgColor={cell.bgColor}
    ariaLabel={cell.ariaLabel}
  />
);

const renderInputCell: CellRenderFunction = ({
  cellId,
  cell,
  rowIndex,
  colIndex,
  isCorrect,
  isPrediction,
  isSubmitted,
  isIncomplete,
  showFeedback,
  ariaLabel,
  onCellChange,
}) => (
  <InputCell
    key={cellId}
    cellId={cellId}
    rowIndex={rowIndex}
    colIndex={colIndex}
    inputType={cell.inputType}
    placeholder={cell.placeholder}
    value={cell.value}
    disabled={cell.disabled}
    showFeedback={(cell.showFeedback ?? showFeedback ?? true) && isSubmitted}
    isCorrect={isCorrect}
    feedback={cell.feedback}
    unique={cell.unique}
    accuracy={cell.accuracy}
    colspan={cell.colspan}
    hint={cell.hint}
    suffix={cell.suffix}
    isPrediction={isPrediction}
    isSubmitted={isSubmitted}
    isIncomplete={isIncomplete}
    ariaLabel={cell.ariaLabel ?? ariaLabel}
    onChange={onCellChange}
  />
);

const renderDropdownCell: CellRenderFunction = ({
  cellId,
  cell,
  rowIndex,
  colIndex,
  isCorrect,
  isPrediction,
  isSubmitted,
  isIncomplete,
  showFeedback,
  ariaLabel,
  onCellChange,
}) => (
  <DropdownCell
    key={cellId}
    cellId={cellId}
    rowIndex={rowIndex}
    colIndex={colIndex}
    placeholder={cell.placeholder}
    options={cell.options}
    value={cell.value}
    disabled={cell.disabled}
    showFeedback={(cell.showFeedback ?? showFeedback ?? true) && isSubmitted}
    isCorrect={isCorrect}
    feedback={cell.feedback}
    unique={cell.unique}
    colspan={cell.colspan}
    hint={cell.hint}
    isPrediction={isPrediction}
    isSubmitted={isSubmitted}
    isIncomplete={isIncomplete}
    ariaLabel={cell.ariaLabel ?? ariaLabel}
    onChange={onCellChange}
  />
);

const renderImageCell: CellRenderFunction = ({ cellId, cell }) => (
  <ImageCell
    key={cellId}
    cellId={cellId}
    image={cell.image}
    alt={cell.alt}
    caption={cell.caption}
    colspan={cell.colspan}
  />
);

const renderImageTitleCell: CellRenderFunction = ({ cellId, cell }) => (
  <ImageCell
    key={cellId}
    cellId={cellId}
    image={cell.image}
    alt={cell.alt}
    caption={cell.caption}
    colspan={cell.colspan}
    isTitle
  />
);

const renderMathJaxCell: CellRenderFunction = ({ cellId, cell }) => (
  <MathJaxCell
    key={cellId}
    cellId={cellId}
    text={cell.text}
    colspan={cell.colspan}
    ariaLabel={cell.ariaLabel}
  />
);

const renderButtonCell: CellRenderFunction = ({
  cellId,
  cell,
  rowIndex,
  colIndex,
  onButtonClick,
}) => (
  <ButtonCell
    key={cellId}
    cellId={cellId}
    rowIndex={rowIndex}
    colIndex={colIndex}
    colspan={cell.colspan}
    disabled={cell.disabled}
    text={cell.text}
    buttonIcon={cell.buttonIcon}
    buttonAriaLabel={cell.buttonAriaLabel}
    actionId={cell.actionId}
    onButtonClick={onButtonClick}
  />
);

/* -------------------------------- Configuration Map -------------------------------- */

export const cellTypeRenderers: Record<TableCellType, CellRenderFunction> = {
  TITLE: renderTitleCell,
  IMAGE_TITLE: renderImageTitleCell,
  FIXED: renderFixedCell,
  LIST: renderListCell,
  TEXT_ALIGNED: renderTextAlignedCell,
  INPUT: renderInputCell,
  DROPDOWN: renderDropdownCell,
  IMAGE: renderImageCell,
  MATH_JAX: renderMathJaxCell,
  BUTTON: renderButtonCell,
};

/* -------------------------------- Main Render Function -------------------------------- */

export const renderTableCell = (
  context: CellRenderContext
): React.ReactNode => {
  const renderer = cellTypeRenderers[context.cell.type] ?? renderFixedCell;
  return renderer(context);
};
